import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import { WD } from '../../../warningDashboard/warningTheme';
import { CARD_BG, FIGURE, HOVER_BG, LEVEL_TONE, OFF_DAY_BG } from '../utils/constants';
import { formatLong, fromISO } from '../utils/dates';
import { levelForPct } from '../utils/hours';
import CountChip from './CountChip';
import NamesTip from './NamesTip';

/** The light card the names open in, over a calendar tag. */
const TIP_SX = {
  bgcolor: '#FFFFFF',
  color: WD.ink,
  border: `1px solid ${WD.border}`,
  boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
  borderRadius: '8px',
  px: 1.75,
  py: 1.25,
  maxWidth: 260,
};

/**
 * One day of the month grid: the date, the team's load that day as % and a bar, and how many people are overloaded / available
 * (hover a tag for the names). Days off and days outside the month are greyed.
 */
export default function CalendarDayCell({ cell, inMonth, isToday, isSelected, lastColumn, lastRow, onSelect }) {
  const level = levelForPct(cell.pct);
  const tone = LEVEL_TONE[level];
  const off = !cell.workingDay;
  const label = off
    ? 'day off'
    : [
        `${Math.round(cell.pct)}% of capacity`,
        cell.names.overloaded.length && `overloaded: ${cell.names.overloaded.map((e) => e.person.name).join(', ')}`,
        cell.names.available.length && `available: ${cell.names.available.map((e) => e.person.name).join(', ')}`,
      ]
        .filter(Boolean)
        .join('; ');

  return (
    <ButtonBase
      onClick={() => onSelect(cell.day)}
      aria-pressed={isSelected}
      aria-label={`${formatLong(cell.day)}: ${label}`}
      sx={{
        display: 'block',
        textAlign: 'left',
        minHeight: { xs: 64, sm: 104 },
        p: { xs: 0.75, sm: 1.25 },
        borderRight: lastColumn ? 'none' : `1px solid ${WD.border}`,
        borderBottom: `1px solid ${WD.border}`,
        bgcolor: isSelected ? WD.greenTint : off || !inMonth ? OFF_DAY_BG : CARD_BG,
        outline: isSelected ? `2px solid ${WD.green}` : 'none',
        outlineOffset: -2,
        opacity: inMonth ? 1 : 0.45,
        '&:hover': { bgcolor: isSelected ? WD.greenTint : HOVER_BG },
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
        <Box
          component="span"
          sx={{
            display: 'grid',
            placeItems: 'center',
            minWidth: 24,
            height: 24,
            px: isToday ? 0.75 : 0,
            borderRadius: 999,
            fontSize: 12.5,
            fontWeight: 700,
            ...FIGURE,
            bgcolor: isToday ? WD.green : 'transparent',
            color: isToday ? '#fff' : off ? WD.inkFaint : WD.ink,
          }}
        >
          {fromISO(cell.day).getDate()}
        </Box>
        {inMonth && !off && (
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: tone.text, ...FIGURE }}>
            {Math.round(cell.pct)}%
          </Typography>
        )}
      </Box>

      {inMonth && off && <Typography sx={{ mt: 0.5, display: { xs: 'none', sm: 'block' }, fontSize: 11, color: WD.inkFaint }}>Day off</Typography>}

      {inMonth && !off && (
        <>
          <Box sx={{ mt: 1, height: 6, borderRadius: 999, bgcolor: '#EFEDE6', overflow: 'hidden' }}>
            <Box sx={{ height: '100%', width: `${Math.min(100, cell.pct)}%`, borderRadius: 999, bgcolor: tone.accent }} />
          </Box>
          <Box sx={{ mt: 1, display: { xs: 'none', sm: 'flex' }, flexWrap: 'wrap', gap: 0.5 }}>
            {['overloaded', 'available'].map((key) =>
              cell.levels[key] > 0 ? (
                <Tooltip key={key} title={<NamesTip level={key} entries={cell.names[key]} />} placement={lastRow ? 'top' : 'bottom'} componentsProps={{ tooltip: { sx: TIP_SX } }}>
                  <span>
                    <CountChip level={key}>
                      {cell.levels[key]} {key === 'overloaded' ? 'overloaded' : 'available'}
                    </CountChip>
                  </span>
                </Tooltip>
              ) : null,
            )}
          </Box>
        </>
      )}
    </ButtonBase>
  );
}
